import {
  EntityTextFilter,
  useEntityList,
} from '@backstage/plugin-catalog-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useDebounce from 'react-use/esm/useDebounce';

export type CatalogIndexColumnId =
  | 'name'
  | 'description'
  | 'system'
  | 'owner'
  | 'type'
  | 'lifecycle'
  | 'namespace'
  | 'targets';

export const catalogIndexAvailableColumns: CatalogIndexColumnId[] = [
  'name',
  'description',
  'system',
  'owner',
  'type',
  'lifecycle',
  'namespace',
  'targets',
];

export const catalogIndexDefaultVisibleColumns: CatalogIndexColumnId[] = [
  ...catalogIndexAvailableColumns,
];

export const catalogIndexColumnLabels: Record<CatalogIndexColumnId, string> = {
  name: 'Name',
  description: 'Description',
  system: 'System',
  owner: 'Owner',
  type: 'Type',
  lifecycle: 'Lifecycle',
  namespace: 'Namespace',
  targets: 'Targets',
};

export const catalogIndexVisibleColumnsStorageKey =
  'catalog-index.visible-columns';

const toQueryValue = (value?: string | string[]) =>
  Array.isArray(value) ? value[0] : value;

export const sanitizeVisibleColumns = (
  value: readonly string[] | undefined,
  availableColumns: readonly CatalogIndexColumnId[] = catalogIndexAvailableColumns,
  fallbackColumns: readonly CatalogIndexColumnId[] = catalogIndexDefaultVisibleColumns,
) => {
  const available = new Set<string>(availableColumns);
  const next = Array.from(
    new Set((value ?? []).filter(column => available.has(column))),
  );

  return next.length ? (next as CatalogIndexColumnId[]) : [...fallbackColumns];
};

const readStoredVisibleColumns = (
  storageKey: string,
  availableColumns: readonly CatalogIndexColumnId[],
  fallbackColumns: readonly CatalogIndexColumnId[],
) => {
  if (typeof window === 'undefined') {
    return [...fallbackColumns];
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);

    if (!rawValue) {
      return [...fallbackColumns];
    }

    const parsed = JSON.parse(rawValue);

    return sanitizeVisibleColumns(
      Array.isArray(parsed)
        ? parsed.filter((value): value is string => typeof value === 'string')
        : undefined,
      availableColumns,
      fallbackColumns,
    );
  } catch {
    return [...fallbackColumns];
  }
};

type UseCatalogIndexStateOptions = {
  availableColumns?: readonly CatalogIndexColumnId[];
  defaultVisibleColumns?: readonly CatalogIndexColumnId[];
  storageKey?: string;
};

export const useCatalogIndexState = (
  options: UseCatalogIndexStateOptions = {},
) => {
  const {
    availableColumns = catalogIndexAvailableColumns,
    defaultVisibleColumns = catalogIndexDefaultVisibleColumns,
    storageKey = catalogIndexVisibleColumnsStorageKey,
  } = options;
  const { limit, paginationMode, queryParameters, setLimit, updateFilters } =
    useEntityList();

  const queryParamText = useMemo(
    () => toQueryValue(queryParameters.text) ?? '',
    [queryParameters.text],
  );
  const [searchText, setSearchText] = useState(queryParamText);
  const [visibleColumns, setVisibleColumnsState] = useState<
    CatalogIndexColumnId[]
  >(() =>
    readStoredVisibleColumns(
      storageKey,
      availableColumns,
      defaultVisibleColumns,
    ),
  );

  useEffect(() => {
    setSearchText(previous =>
      previous === queryParamText ? previous : queryParamText,
    );
  }, [queryParamText]);

  useDebounce(
    () => {
      const nextSearch = searchText.trim();

      updateFilters({
        text: nextSearch ? new EntityTextFilter(nextSearch) : undefined,
      });
    },
    250,
    [searchText, updateFilters],
  );

  useEffect(() => {
    setVisibleColumnsState(previous =>
      sanitizeVisibleColumns(previous, availableColumns, defaultVisibleColumns),
    );
  }, [availableColumns, defaultVisibleColumns]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(storageKey, JSON.stringify(visibleColumns));
    } catch {
      // Ignore storage failures and keep the in-memory selection.
    }
  }, [storageKey, visibleColumns]);

  const setVisibleColumns = useCallback(
    (
      update:
        | CatalogIndexColumnId[]
        | ((prev: CatalogIndexColumnId[]) => CatalogIndexColumnId[]),
    ) => {
      setVisibleColumnsState(previous =>
        sanitizeVisibleColumns(
          typeof update === 'function' ? update(previous) : update,
          availableColumns,
          defaultVisibleColumns,
        ),
      );
    },
    [availableColumns, defaultVisibleColumns],
  );

  const toggleVisibleColumn = useCallback(
    (columnId: CatalogIndexColumnId) => {
      setVisibleColumns(previous =>
        previous.includes(columnId)
          ? previous.filter(id => id !== columnId)
          : [...previous, columnId],
      );
    },
    [setVisibleColumns],
  );

  const resetVisibleColumns = useCallback(() => {
    setVisibleColumns([...defaultVisibleColumns]);
  }, [defaultVisibleColumns, setVisibleColumns]);

  const isColumnVisible = useCallback(
    (columnId: CatalogIndexColumnId) => visibleColumns.includes(columnId),
    [visibleColumns],
  );

  return {
    isColumnVisible,
    pageSize: limit,
    paginationMode,
    resetVisibleColumns,
    searchText,
    setPageSize: setLimit,
    setSearchText,
    setVisibleColumns,
    toggleVisibleColumn,
    visibleColumns,
  };
};
